import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Stack, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, InputLabel, FormControl, Drawer, Card, CardContent,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import { Calendar, dateFnsLocalizer, type View, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, startOfDay, endOfDay, startOfMonth, endOfMonth, endOfWeek } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { calendarApi, calendarSourcesApi, calendarConnectApi, driversApi } from '../api/services';
import type { CalendarEvent, CalendarSource, AnnualStrategicDriver, RestaurantResult } from '../types/models';
import RestaurantSearchDialog from '../components/RestaurantSearchDialog';
import ConnectCalendarCard from '../components/ConnectCalendarCard';
import CalendarSourcesCard from '../components/CalendarSourcesCard';
import CalendarEventDetail from '../components/CalendarEventDetail';
import { getCalendarStyles } from '../theme/calendarStyles';

// ---------------------------------------------------------------------------
// date-fns localizer setup
// ---------------------------------------------------------------------------
const locales = { 'en-GB': enGB };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

// ---------------------------------------------------------------------------
// Mapped event type for react-big-calendar
// ---------------------------------------------------------------------------
interface CalendarBigEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: CalendarEvent;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute the visible range for a given date + view. */
function getVisibleRange(date: Date, view: View): { from: Date; to: Date } {
  switch (view) {
    case Views.DAY:
      return { from: startOfDay(date), to: endOfDay(date) };
    case Views.WEEK:
      return {
        from: startOfWeek(date, { weekStartsOn: 1 }),
        to: endOfWeek(date, { weekStartsOn: 1 }),
      };
    case Views.MONTH: {
      // Month view also shows overflow weeks, so pad a week in each direction
      const ms = startOfMonth(date);
      const me = endOfMonth(date);
      return {
        from: startOfWeek(ms, { weekStartsOn: 1 }),
        to: endOfWeek(me, { weekStartsOn: 1 }),
      };
    }
    default:
      return { from: startOfDay(date), to: endOfDay(date) };
  }
}

function formatRangeLabel(date: Date, view: View): string {
  switch (view) {
    case Views.DAY:
      return format(date, 'EEEE, d MMMM yyyy');
    case Views.WEEK: {
      const ws = startOfWeek(date, { weekStartsOn: 1 });
      const we = endOfWeek(date, { weekStartsOn: 1 });
      if (ws.getMonth() === we.getMonth()) {
        return `${format(ws, 'd')} - ${format(we, 'd MMMM yyyy')}`;
      }
      if (ws.getFullYear() === we.getFullYear()) {
        return `${format(ws, 'd MMM')} - ${format(we, 'd MMM yyyy')}`;
      }
      return `${format(ws, 'd MMM yyyy')} - ${format(we, 'd MMM yyyy')}`;
    }
    case Views.MONTH:
      return format(date, 'MMMM yyyy');
    default:
      return format(date, 'd MMMM yyyy');
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function CalendarPage() {
  const theme = useTheme();
  const calendarSx = useMemo(() => getCalendarStyles(theme), [theme]);

  // Data state
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeDrivers, setActiveDrivers] = useState<AnnualStrategicDriver[]>([]);

  // Calendar navigation
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentView, setCurrentView] = useState<View>(Views.WEEK);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sync / connection
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [connectionBanner, setConnectionBanner] = useState<string | null>(null);

  // Create Event state
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [oauthSources, setOauthSources] = useState<CalendarSource[]>([]);
  const [createEventData, setCreateEventData] = useState({
    title: '',
    startTime: '',
    endTime: '',
    location: '',
    description: '',
    eventType: 'Meeting',
    attendees: '',
    sourceId: '',
  });
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [createEventMessage, setCreateEventMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Restaurant search state
  const [restaurantDialogOpen, setRestaurantDialogOpen] = useState(false);

  // Event detail state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadEventsForRange = useCallback(async (from: Date, to: Date) => {
    setLoading(true);
    try {
      const fromStr = format(from, 'yyyy-MM-dd');
      const toStr = format(to, 'yyyy-MM-dd');
      setEvents(await calendarApi.getByRange(fromStr, toStr));
    } catch (err) {
      console.error('Failed to load events', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCurrentRange = useCallback(() => {
    const { from, to } = getVisibleRange(currentDate, currentView);
    loadEventsForRange(from, to);
  }, [currentDate, currentView, loadEventsForRange]);

  // Load events whenever date or view changes
  useEffect(() => {
    loadCurrentRange();
  }, [loadCurrentRange]);

  // Load strategic drivers on mount
  useEffect(() => {
    driversApi.getActive()
      .then(setActiveDrivers)
      .catch(() => {});
  }, []);

  // Load OAuth sources and auto-sync connected calendars on page load
  useEffect(() => {
    const loadOauthSources = async () => {
      try {
        const all = await calendarSourcesApi.getAll();
        const oauth = all.filter((s: CalendarSource) => s.sourceType === 'Microsoft365' || s.sourceType === 'Google');
        setOauthSources(oauth);
        if (oauth.length > 0 && !createEventData.sourceId) {
          setCreateEventData(prev => ({ ...prev, sourceId: oauth[0].id }));
        }

        // Auto-sync all connected sources in background
        const connected = oauth.filter((s: CalendarSource) => s.isConnected && !s.needsReconnect);
        for (const source of connected) {
          calendarConnectApi.sync(source.id).catch(() => {});
        }
        // Reload events after sync
        if (connected.length > 0) {
          setTimeout(() => loadCurrentRange(), 2000);
        }
      } catch {
        // Silently fail -- no OAuth sources available
      }
    };
    loadOauthSources();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for ?connected= query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected === 'microsoft' || connected === 'google') {
      const label = connected === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      setConnectionBanner(`${label} connected successfully! Your calendar will sync automatically.`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Event mapping for react-big-calendar
  // -------------------------------------------------------------------------

  const mappedEvents: CalendarBigEvent[] = useMemo(() =>
    events.map(event => ({
      id: event.id,
      title: event.title,
      start: new Date(event.startTime),
      end: new Date(event.endTime),
      allDay: event.isAllDay,
      resource: event,
    })),
    [events],
  );

  // -------------------------------------------------------------------------
  // Navigation handlers
  // -------------------------------------------------------------------------

  const handleNavigateBack = () => {
    setCurrentDate(prev => {
      switch (currentView) {
        case Views.DAY: return subDays(prev, 1);
        case Views.WEEK: return subWeeks(prev, 1);
        case Views.MONTH: return subMonths(prev, 1);
        default: return prev;
      }
    });
  };

  const handleNavigateForward = () => {
    setCurrentDate(prev => {
      switch (currentView) {
        case Views.DAY: return addDays(prev, 1);
        case Views.WEEK: return addWeeks(prev, 1);
        case Views.MONTH: return addMonths(prev, 1);
        default: return prev;
      }
    });
  };

  const handleNavigateToday = () => {
    setCurrentDate(new Date());
  };

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: View | null) => {
    if (newView) setCurrentView(newView);
  };

  // -------------------------------------------------------------------------
  // react-big-calendar callbacks
  // -------------------------------------------------------------------------

  const handleRangeChange = useCallback((range: Date[] | { start: Date; end: Date }) => {
    if (Array.isArray(range)) {
      if (range.length > 0) {
        loadEventsForRange(startOfDay(range[0]), endOfDay(range[range.length - 1]));
      }
    } else {
      loadEventsForRange(range.start, range.end);
    }
  }, [loadEventsForRange]);

  const handleSelectEvent = useCallback((event: CalendarBigEvent) => {
    setSelectedEvent(event.resource);
    setEventDetailOpen(true);
  }, []);

  const eventPropGetter = useCallback((event: CalendarBigEvent) => {
    const evt = event.resource as CalendarEvent;
    let backgroundColor = theme.palette.primary.main;
    let borderColor = theme.palette.primary.main;
    if (evt.isPriorityMeeting || evt.isPrivate) {
      backgroundColor = theme.palette.error.main;
      borderColor = theme.palette.error.main;
    } else if (evt.isAllDay) {
      backgroundColor = theme.palette.warning.main;
      borderColor = theme.palette.warning.main;
    }
    return {
      style: {
        backgroundColor,
        borderColor,
        borderRadius: 4,
        opacity: 0.9,
        color: '#fff',
        border: 'none',
        fontSize: '0.8rem',
      },
    };
  }, [theme]);

  // -------------------------------------------------------------------------
  // Restaurant select handler
  // -------------------------------------------------------------------------

  const handleRestaurantSelect = (restaurant: RestaurantResult) => {
    setCreateEventData(prev => ({
      ...prev,
      title: `Dinner at ${restaurant.name}`,
      location: restaurant.address || restaurant.name,
      description: [
        restaurant.name,
        restaurant.rating ? `Rating: ${restaurant.rating}/5` : null,
        restaurant.address,
        restaurant.googleMapsUrl ? `Google Maps: ${restaurant.googleMapsUrl}` : null,
      ].filter(Boolean).join('\n'),
    }));
    setCreateEventOpen(true);
  };

  // -------------------------------------------------------------------------
  // Create event handler
  // -------------------------------------------------------------------------

  const handleCreateEvent = async () => {
    if (!createEventData.title.trim() || !createEventData.startTime || !createEventData.endTime || !createEventData.sourceId) return;
    setCreatingEvent(true);
    setCreateEventMessage(null);
    try {
      await calendarSourcesApi.createEvent(createEventData.sourceId, {
        title: createEventData.title,
        startTime: createEventData.startTime,
        endTime: createEventData.endTime,
        location: createEventData.location || undefined,
        description: createEventData.description || undefined,
        eventType: createEventData.eventType,
        attendees: createEventData.attendees
          ? createEventData.attendees.split(',').map(a => a.trim()).filter(Boolean)
          : undefined,
      });
      setCreateEventOpen(false);
      setCreateEventData({
        title: '',
        startTime: '',
        endTime: '',
        location: '',
        description: '',
        eventType: 'Meeting',
        attendees: '',
        sourceId: oauthSources.length > 0 ? oauthSources[0].id : '',
      });
      setCreateEventMessage({ type: 'success', text: 'Event created successfully. Refreshing calendar...' });
      loadCurrentRange();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create event';
      setCreateEventMessage({ type: 'error', text: typeof msg === 'string' ? msg : 'Failed to create event' });
    } finally {
      setCreatingEvent(false);
    }
  };

  // -------------------------------------------------------------------------
  // Stats (for drawer)
  // -------------------------------------------------------------------------

  const meetingMinutes = events
    .filter(e => !e.isAllDay)
    .reduce((sum, e) => sum + (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 60000, 0);

  const priorityMeetingCount = events.filter(e => e.isPriorityMeeting || e.isPrivate).length;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
      {/* Header bar */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1,
      }}>
        {/* Left: Title */}
        <Typography variant="h4" sx={{ mr: 2 }}>Calendar</Typography>

        {/* Centre: Date navigation */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, justifyContent: 'center' }}>
          <IconButton size="small" onClick={handleNavigateBack} aria-label="Previous">
            <ChevronLeftIcon />
          </IconButton>
          <Button size="small" variant="outlined" onClick={handleNavigateToday}>
            Today
          </Button>
          <IconButton size="small" onClick={handleNavigateForward} aria-label="Next">
            <ChevronRightIcon />
          </IconButton>
          <Typography variant="h6" sx={{ mx: 1, whiteSpace: 'nowrap', fontWeight: 500 }}>
            {formatRangeLabel(currentDate, currentView)}
          </Typography>
        </Stack>

        {/* Right: View toggle + actions + settings */}
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={currentView}
            exclusive
            onChange={handleViewChange}
            size="small"
          >
            <ToggleButton value={Views.DAY}>Day</ToggleButton>
            <ToggleButton value={Views.WEEK}>Week</ToggleButton>
            <ToggleButton value={Views.MONTH}>Month</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            size="small"
            startIcon={<EventIcon />}
            onClick={() => setCreateEventOpen(true)}
            disabled={oauthSources.length === 0}
            title={oauthSources.length === 0 ? 'Connect a Microsoft 365 or Google calendar to create events' : 'Create a new calendar event'}
          >
            Create Event
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestaurantIcon />}
            onClick={() => setRestaurantDialogOpen(true)}
          >
            Book Restaurant
          </Button>

          <IconButton onClick={() => setDrawerOpen(true)} aria-label="Calendar settings">
            <SettingsIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Banners / alerts */}
      {connectionBanner && (
        <Alert severity="success" sx={{ mb: 1 }} icon={<CheckCircleIcon />} onClose={() => setConnectionBanner(null)}>
          {connectionBanner}
        </Alert>
      )}
      {syncMessage && (
        <Alert severity={syncMessage.type} sx={{ mb: 1 }} onClose={() => setSyncMessage(null)}>
          {syncMessage.text}
        </Alert>
      )}
      {createEventMessage && (
        <Alert severity={createEventMessage.type} sx={{ mb: 1 }} onClose={() => setCreateEventMessage(null)}>
          {createEventMessage.text}
        </Alert>
      )}

      {/* Calendar */}
      <Box sx={{ flexGrow: 1, minHeight: 0, ...calendarSx }}>
        <Calendar<CalendarBigEvent>
          localizer={localizer}
          events={mappedEvents}
          date={currentDate}
          view={currentView}
          onNavigate={setCurrentDate}
          onView={setCurrentView}
          onRangeChange={handleRangeChange}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventPropGetter}
          style={{ height: '100%' }}
          views={[Views.DAY, Views.WEEK, Views.MONTH]}
          toolbar={true}
          popup
          selectable={false}
          scrollToTime={new Date(1970, 0, 1, 7, 0, 0)}
        />
      </Box>

      {/* Settings Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 380, p: 2 } }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Calendar Settings</Typography>

        <ConnectCalendarCard />

        <Box sx={{ mt: 2 }}>
          <CalendarSourcesCard />
        </Box>

        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Current View Stats
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Events</Typography>
                <Typography variant="h6">{events.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Meeting Time</Typography>
                <Typography variant="h6">{Math.round(meetingMinutes / 60 * 10) / 10}h</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Priority Meetings</Typography>
                <Typography variant="h6" color="error.main">{priorityMeetingCount}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Drawer>

      {/* Event Detail Dialog */}
      <CalendarEventDetail
        event={selectedEvent}
        open={eventDetailOpen}
        onClose={() => {
          setEventDetailOpen(false);
          setSelectedEvent(null);
        }}
        onDriverLinked={loadCurrentRange}
        drivers={activeDrivers}
      />

      {/* Create Event Dialog */}
      <Dialog open={createEventOpen} onClose={() => setCreateEventOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Calendar Event</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Calendar</InputLabel>
              <Select
                value={createEventData.sourceId}
                label="Calendar"
                onChange={e => setCreateEventData(prev => ({ ...prev, sourceId: e.target.value }))}
              >
                {oauthSources.map(source => (
                  <MenuItem key={source.id} value={source.id}>
                    {source.name} ({source.sourceType === 'Microsoft365' ? 'Microsoft 365' : 'Google'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Title"
              fullWidth
              size="small"
              value={createEventData.title}
              onChange={e => setCreateEventData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Meeting title"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start"
                type="datetime-local"
                fullWidth
                size="small"
                value={createEventData.startTime}
                onChange={e => setCreateEventData(prev => ({ ...prev, startTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End"
                type="datetime-local"
                fullWidth
                size="small"
                value={createEventData.endTime}
                onChange={e => setCreateEventData(prev => ({ ...prev, endTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Location"
              fullWidth
              size="small"
              value={createEventData.location}
              onChange={e => setCreateEventData(prev => ({ ...prev, location: e.target.value }))}
              placeholder="Office, Zoom link, etc."
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              rows={3}
              value={createEventData.description}
              onChange={e => setCreateEventData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Event description..."
            />
            <FormControl fullWidth size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={createEventData.eventType}
                label="Event Type"
                onChange={e => setCreateEventData(prev => ({ ...prev, eventType: e.target.value }))}
              >
                <MenuItem value="Meeting">Meeting</MenuItem>
                <MenuItem value="FocusTime">Focus Time</MenuItem>
                <MenuItem value="Travel">Travel</MenuItem>
                <MenuItem value="Reservation">Reservation</MenuItem>
                <MenuItem value="Personal">Personal</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Attendees"
              fullWidth
              size="small"
              value={createEventData.attendees}
              onChange={e => setCreateEventData(prev => ({ ...prev, attendees: e.target.value }))}
              placeholder="email1@example.com, email2@example.com"
              helperText="Comma-separated email addresses"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEventOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateEvent}
            disabled={creatingEvent || !createEventData.title.trim() || !createEventData.startTime || !createEventData.endTime || !createEventData.sourceId}
          >
            {creatingEvent ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restaurant Search Dialog */}
      <RestaurantSearchDialog
        open={restaurantDialogOpen}
        onClose={() => setRestaurantDialogOpen(false)}
        onSelect={handleRestaurantSelect}
      />
    </Box>
  );
}
